import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { setPageHeaderTextActionCreator } from 'plugins/store/reducers/common.reducer';
import withForm from 'hoc/withForm';
import { withSnackbar } from 'notistack';
import { getOrganizationDetails, updateOrganization, createOrganization } from 'services/organization.service';
import layoutNames from 'utils/enums/layout-names.enum';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import RouteLeavingGuard from 'components/RouteLeavingGuard/RouteLeavingGuard';
import withStyles from '@material-ui/core/styles/withStyles';
import OrganizationAuthenticationSection from 'components/Organization/OrganizationAuthenticationSection';
import { setAlertActionCreator } from 'plugins/store/reducers/common.reducer';

// @material-ui/icons
import BusinessOutlinedIcon from '@material-ui/icons/BusinessOutlined';

// core components
import AuditLog from 'components/AuditLog/AuditLog';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardIcon from 'components/Card/CardIcon';
import SnackMessage from 'components/Snackbar/SnackMessage';
import NavPills from 'components/NavPills/NavPills';
import OrganizationEditForm from 'components/Organization/OrganizationEditForm';
import RegistrationList from '../../components/RegistrationList/RegistrationList';

import styles from 'assets/jss/material-dashboard-pro-react/views/organizationEditFormStyle';


class EditOrganization extends Component {
	state = {
		id: undefined,
		organization: {},
		activeTabIndex: 0,
		isLoading: true,
		orList: [],
	};

	async componentDidMount() {
		await this.refreshDetails();
		const id = this.props.match?.params?.id;
		let organization;

		if (id) {
			this.setState({
				id,
			});

			organization = await this.getOrganizationDetails(id);
		}
		else {
			this.props.setPageHeaderText('Create Organization');
			organization = {
				name: '',
				url: '',
				createdBy: '',
				createdAt: '',
				modifiedBy: '',
				updatedAt: '',
				orList: [],
				features: {
					marketing: {
						multicast: false,
						broadcast: false,
					},
					goodCalls: {
						defaultNotifications: false,
						customNotifications: false,
						customLogos: false,
					},
					fraudCalls: {
						customLogos: false,
					},
					scheduler: {
						scheduleCall: false,
					},
					analytics: {
						view: false,
						alarms: false,
						customizeThresholds: false,
					},
					rating: {
						rateCall: false,
					},
				}
			}
		}

		this.setState({
			organization,
			isLoading: false,
		});
	}

	refreshDetails = async () => {
		try {
			const id = this.props.match?.params?.id;

			this.setState({
				isLoading: true,
			});
			const result = await getOrganizationDetails(id);
			const validOrList = result.orList
				.filter((item) => item.phoneNumber);
			this.setState(() => {
				return {
					isLoading: false,
					organization: result,
					callFromOptions: validOrList,
				};
			});
		} catch (error) {
			console.log(error);
		}
	}

	componentWillUnmount() {
		this.props.setPageHeaderText('');
	}

	getOrganizationDetails = async (id) => {
		const { reset } = this.props.formMethods;
		const organization = await getOrganizationDetails(id);

		this.props.setPageHeaderText(organization.name);
		reset(organization);
		return organization;
	}

	onCredentialsGenerated = async (appKey) => {
		this.setState(prevState => ({
			organization: {
					...prevState.organization,
					appKey,
			}
		}));
	}

	onSubmit = async (data) => {
		const {
			formMethods,
			enqueueSnackbar,
		} = this.props;

		const { reset } = formMethods;
		try {
			if(this.state.id) {
				await updateOrganization(data, this.state.id)
				reset(data);
				this.onSubmitted();
			} else {
				const createdOrg = await createOrganization(data);
				reset(data);
				this.openOnSaveDialog(createdOrg.appKey, createdOrg.appSecret);
			}
		} catch (e) {
			enqueueSnackbar(e.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
		}
	};

	onSubmitted = async (data) => {
		const {
			history,
			enqueueSnackbar,
		} = this.props;

		enqueueSnackbar('Changes have been saved successfully', {
			content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
		});

		history.replace({
			pathname: `/${layoutNames.portal}/organizations`,
		});
	}

	toggleFeature(section, feature, checked) {
		this.setState(prevState => ({
			...prevState,
			organization: {
					...prevState.organization,
					features: {
							...prevState.organization.features,
							[section]: {
								 ...prevState.organization.features[section],
								 [feature]: checked
							}
					}
			}
	}))
	}

	openOnSaveDialog = (appKey, appSecret) => {
		this.props.setAlert({
			type: 'info',
			title: 'Organization has been created',
			confirmBtnText: 'Ok, I have copied the credentials',
			confirmBtnColor: 'success',
			width: '40em',
			children: this.onSaveDialogWarning(appKey, appSecret),
			onConfirm: () => {
				this.onSubmitted();
				this.props.setAlert(null);
			},
		});
	}

	onSaveDialogWarning = (appKey, appSecret) => {
		const { classes } = this.props;

		return (
			<div className={classes.left}>
				<div>Use the Application key and Application Secret for authentication of the organization Portal at the License service.</div>
				<div>App secret is displayed only once due to security reasons. Please copy it now. You may generate a new pair of app key and secret anytime at the Edit Organization page.</div>
				<div><b>Application Key:</b> {appKey}</div>
				<div><b>Application Secret:</b> {appSecret}</div>
			</div>
		);
	}

	onPhoneNumbersListChange = async () => {
		try {
			const id = this.props.match?.params?.id;
			const result = await getOrganizationDetails(id);
			const validOrList = result.orList.filter((item) => item.phoneNumber);
			this.setState((prevState) => ({
				organization: {
					...prevState.organization,
					orList: validOrList,
				},
			}));
		} catch (error) {
			console.log(error);
		}
	}

	render() {
		const { history, classes, formMethods } = this.props;

		const { formState } = formMethods;

		const { isDirty } = formState;

		const {
			id,
			organization: {
				createdBy,
				createdAt,
				modifiedBy,
				updatedAt,
				orList,
			}
		} = this.state;

		const tabs = [
			{
				tabButton: "Details",
				tabContent: (
					<OrganizationEditForm
						formMethods={formMethods}
						onSubmit={this.onSubmit}
						id={this.state.id}
						organization={this.state.organization}
						toggleFeature={(...args) => { this.toggleFeature(...args); }}
					/>
				)
			},
			id &&	{
				tabButton: "Authentication",
				tabContent: (
					<OrganizationAuthenticationSection
						classes={classes}
						id={id}
						appKey={this.state.organization?.appKey}
						onCredentialsGenerated={this.onCredentialsGenerated}
					/>
				)
			},
			id && {
				tabButton: "Audit Log",
				tabContent: (
					<AuditLog
						createdBy={createdBy}
						createdAt={createdAt}
						modifiedBy={modifiedBy}
						modifiedAt={updatedAt}
					/>
				)
			},
			id && {
				tabButton: "Registered Numbers",
				tabContent: (
					<RegistrationList
					parentOrganization={this.state.organization}
					data={orList}
					onPhoneNumbersListChange={this.onPhoneNumbersListChange}
					/>
				)
			}
		].filter((tab) => tab);

		return (
			<>
				{
					this.state.isLoading && <div className={classes.loadingIndicator}><LoadingIndicator /></div>
				}
				{!this.state.isLoading &&
					<div>
						<GridContainer>
							<GridItem xs={12} sm={12} md={12}>
								<Card>
									<CardHeader color="primary" icon>
										<CardIcon color="primary">
											<BusinessOutlinedIcon />
										</CardIcon>
									</CardHeader>
									<CardBody>
										<NavPills
											color="warning"
											tabs={tabs}
										/>
									</CardBody>
								</Card>
							</GridItem>
						</GridContainer>
						<RouteLeavingGuard
							when={isDirty}
							navigate={path => history.push(path)}
							shouldBlockNavigation={() => {
								if (isDirty) {
									return true;
								}
								return false
							}}
						/>
					</div>
				}
			</>
		);
	}
}

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
	setAlert: (dialog) => dispatch(setAlertActionCreator(dialog)),
	setPageHeaderText: bindActionCreators(
		setPageHeaderTextActionCreator,
		dispatch
	),
});

export default compose(
	withStyles(styles),
	connect(mapStateToProps, mapDispatchToProps),
	withSnackbar,
	withForm,
)(EditOrganization);
